import type { SportCategoryMatch } from '@bz/request/src/apis';
import { createLazyMemo } from '@solid-primitives/memo';

import { formatTwoDigit } from '~/utils';
import { type SportType, SportTypeMap, SoccerStatus, EventStatus } from '~/constants';

export const DayTimeMillisecond = 24 * 60 * 60 * 1000;

/**
 * 足球比赛时间格式化
 * @param match
 * @returns
 */
const useSoccerTimeFormat = (match: SportCategoryMatch, matchStatus: SoccerStatus) => {
  const tick = createTick();

  return createLazyMemo<string>(() => {
    tick();

    const { matchTime, stoppageTime, stoppageTimeAnnounced } = match.sportEventStatus?.clock ?? {};
    const [matchMinutes, matchSeconds] = matchTime?.split(':').map(Number) ?? [0, 0];
    const [stoppageMinutes, stoppageSeconds] = stoppageTime?.split(':').map(Number) ?? [0, 0];
    const [stoppageTimeAnnouncedMinutes, stoppageTimeAnnouncedSeconds] = stoppageTimeAnnounced
      ?.split(':')
      .map(Number) ?? [0, 0];
    /** 向上取整一分钟 */
    let matchMinutesElapsed = matchMinutes + Math.ceil(matchSeconds / 60);
    let stoppageMinutesElapsed = stoppageMinutes + Math.ceil(stoppageSeconds / 60);
    let stoppageTimeAnnouncedMinutesElapsed =
      stoppageTimeAnnouncedMinutes + Math.ceil(stoppageTimeAnnouncedSeconds / 60);

    switch (matchStatus) {
      /** 上半场/加时 */
      case SoccerStatus.FIRST_HALF:
        const firstHalfBaseTime =
          matchMinutesElapsed >= 45 && stoppageMinutesElapsed > 0
            ? `45+${formatTwoDigit(stoppageMinutesElapsed)}'`
            : `${formatTwoDigit(matchMinutesElapsed)}'`;
        const firstHalfStoppageInfo =
          stoppageTimeAnnouncedMinutes > 0
            ? ` (+${stoppageTimeAnnouncedMinutesElapsed}mins)`
            : stoppageTimeAnnouncedSeconds > 0
              ? ` (+${stoppageTimeAnnouncedSeconds}s)`
              : '';
        return firstHalfBaseTime + firstHalfStoppageInfo;

      /** 下半场/加时 */
      case SoccerStatus.SECOND_HALF:
        const secondHalfBaseTime =
          matchMinutesElapsed >= 90 && stoppageMinutesElapsed > 0
            ? `90+${formatTwoDigit(stoppageMinutesElapsed)}'`
            : `${formatTwoDigit(matchMinutesElapsed)}'`;
        const secondHalfStoppageInfo =
          stoppageTimeAnnouncedMinutes > 0
            ? ` (+${stoppageTimeAnnouncedMinutesElapsed}mins)`
            : stoppageTimeAnnouncedSeconds > 0
              ? ` (+${stoppageTimeAnnouncedSeconds}s)`
              : '';
        return secondHalfBaseTime + secondHalfStoppageInfo;
      default:
        return '';
    }
  });
};

/**
 * 篮球比赛时间格式化
 * @param match
 * @returns
 */
const useBasketballTimeFormat = (match: SportCategoryMatch) => {
  const tick = createTick();

  let totalSeconds = 0;
  let lastUpdateTime = Date.now();
  let lastStopState = false;

  return createLazyMemo(() => {
    tick();

    /** 获取最新的时间数据，优先使用 WebSocket 数据 */
    // @ts-expect-error 数据里有添加推送的时间戳
    const wsTimestamp = match?.timestamp ?? Date.now();
    const remainingTime = match?.sportEventStatus?.clock?.remainingTimeInPeriod;

    /** 当 WebSocket 数据更新时，重新设置倒计时起点 */
    if (remainingTime) {
      const [newMinutes, newSeconds] = remainingTime.split(':').map(Number);
      const newTotalSeconds = newMinutes * 60 + newSeconds;

      /** 只有当时间发生变化时才更新 */
      if (newTotalSeconds !== totalSeconds) {
        totalSeconds = newTotalSeconds;
        lastUpdateTime = wsTimestamp;
      }
    }

    /** 没有剩余时间时返回空字符串 */
    if (totalSeconds <= 0) return '';

    /** 是否暂停 */

    const isStop = !!match?.sportEventStatus?.clock?.stopped;

    /** 检测暂停状态变化：从暂停恢复时，更新 lastUpdateTime */
    if (lastStopState && !isStop) {
      lastUpdateTime = wsTimestamp;
    }
    lastStopState = isStop;

    /** 计算当前剩余时间 */
    let currentSeconds = totalSeconds;
    if (!isStop) {
      const elapsedSeconds = Math.floor((Date.now() - lastUpdateTime) / 1000);
      currentSeconds = Math.max(0, totalSeconds - elapsedSeconds);

      /** 倒计时结束 */
      if (currentSeconds <= 0) return '';
    }

    /** 格式化时间 */
    const m = Math.floor(currentSeconds / 60);
    const s = currentSeconds % 60;

    return `${formatTwoDigit(m)}:${formatTwoDigit(s)}`;
  });
};

/**
 * 五人足球比赛时间格式化
 * @param match
 * @returns
 */
const useFutsalTimeFormat = (match: SportCategoryMatch) => {
  const tick = createTick();

  let totalSeconds = 0;
  let lastUpdateTime = Date.now();
  let lastStopState = false;

  return createLazyMemo(() => {
    tick();

    /** 获取最新的时间数据，优先使用 WebSocket 数据 */
    // @ts-expect-error socket 数据里有添加推送的时间戳
    const wsTimestamp = match?.timestamp ?? Date.now();
    const remainingTime = match?.sportEventStatus?.clock?.matchTime;

    /** 当 WebSocket 数据更新时，重新设置倒计时起点 */
    if (remainingTime) {
      const [newMinutes, newSeconds] = remainingTime.split(':').map(Number);
      const newTotalSeconds = newMinutes * 60 + newSeconds;

      /** 只有当时间发生变化时才更新 */
      if (newTotalSeconds !== totalSeconds) {
        totalSeconds = newTotalSeconds;
        lastUpdateTime = wsTimestamp;
      }
    }

    /** 没有剩余时间时返回空字符串 */
    if (totalSeconds <= 0) return '';

    /** 是否暂停 */

    const isStop = !!match?.sportEventStatus?.clock?.stopped;

    /** 检测暂停状态变化：从暂停恢复时，更新 lastUpdateTime */
    if (lastStopState && !isStop) {
      lastUpdateTime = wsTimestamp;
    }
    lastStopState = isStop;

    /** 计算当前剩余时间 */
    let currentSeconds = totalSeconds;
    if (!isStop) {
      const elapsedSeconds = Math.floor((Date.now() - lastUpdateTime) / 1000);
      currentSeconds = Math.max(0, totalSeconds - elapsedSeconds);

      /** 倒计时结束 */
      if (currentSeconds <= 0) return '';
    }

    /** 格式化时间 */
    const m = Math.floor(currentSeconds / 60);
    const s = currentSeconds % 60;

    return `${formatTwoDigit(m)}:${formatTwoDigit(s)}`;
  });
};

/**
 * Live Time 根据不同体育类型进行格式化
 * @param match
 * @param type
 * @returns
 */
export function useLiveTimeFormat(match: SportCategoryMatch, matchStatus: SoccerStatus, type?: SportType) {
  switch (type) {
    case SportTypeMap.SOCCER:
      return useSoccerTimeFormat(match, matchStatus);
    case SportTypeMap.BASKETBALL:
    case SportTypeMap.ICE_HOCKEY:
    case SportTypeMap.AMERICAN_FOOTBALL:
    case SportTypeMap.BOXING:
    case SportTypeMap.AUSSIE_RULES:
    case SportTypeMap.HANDBALL:
      return useBasketballTimeFormat(match);
    case SportTypeMap.FUTSAL:
      return useFutsalTimeFormat(match);
    default:
      return () => match.sportEventStatus?.clock?.matchTime ?? '';
  }
}

/**
 * Upcoming Time 固定按未来时间进行格式化
 * @param scheduledTime
 * @returns
 */
export function useUpcomingTimeFormat(scheduledTime: number | (() => number)) {
  const tick = createTick();
  return createLazyMemo(() => {
    const now = tick();
    const schedule = typeof scheduledTime === 'function' ? scheduledTime() : scheduledTime;

    const scheduleMs = schedule < 100000000000 ? schedule * 1000 : schedule;

    const today = new Date(now);
    const tomorrow = new Date(now + DayTimeMillisecond);
    const feature = new Date(scheduleMs);

    /** 今天，显示 Today */
    if (today.toDateString() === feature.toDateString()) {
      const matchTime = feature.toLocaleTimeString(setting.lang, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      });
      return `${t('Today')}\n${matchTime}`;
    } else if (tomorrow.toDateString() === feature.toDateString()) {
      /** 明天，显示 Tomorrow */
      const matchTime = feature.toLocaleTimeString(setting.lang, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      });
      return `${t('Tomorrow')}\n${matchTime}`;
    } else {
      /** 其他，显示具体日期 */
      const datePart = feature.toLocaleDateString(setting.lang, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour12: false,
      });
      const matchTime = feature.toLocaleTimeString(setting.lang, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      });
      return `${datePart}\n${matchTime}`;
    }
  });
}

export function useMatchFormatTime(match: SportCategoryMatch, type?: SportType) {
  const status = createMemo(() => match.status);

  switch (status()) {
    case EventStatus.LIVE_EVENTS:
      return useLiveTimeFormat(match, match.matchStatus, type);
    case EventStatus.UPCOMING:
      return useUpcomingTimeFormat(() => match.scheduledTime);
    default:
      return () => '';
  }
}

export function useMatchCountdownTimeFormat(time = 1763736600000) {
  const tick = createTick();

  return createLazyMemo(() => {
    const now = tick();
    const diff = time - now;

    // 如果时间已过，返回全0
    if (diff <= 0) {
      return {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      };
    }

    // 计算天、小时、分钟、秒
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((diff % (60 * 1000)) / 1000);

    return {
      days: formatTwoDigit(days),
      hours: formatTwoDigit(hours),
      minutes: formatTwoDigit(minutes),
      seconds: formatTwoDigit(seconds),
    };
  });
}
